/**
 * The dashboard's navigation: plain text links in a light rail, the same shape the back office
 * uses, because the two are sibling administration tools and an administrator who opens both
 * should not have to learn two layouts.
 *
 * No icons. Five destinations with names that say exactly what they are, and a glyph beside
 * "Playground" teaches nobody anything.
 *
 * All five are offered to everyone who can sign in. Authorization is on the server — every
 * endpoint under `/admin/v1` is role-gated, and a compliance user who opens Tools sees it
 * read-only because their saves are refused, not because the link was hidden.
 */

import { useTranslation } from "react-i18next";

import { RouteLink, cx } from "@nab/shared";

export interface BotNavProps {
  routes: { id: string; path: string }[];
  activeId: string;
}

export function BotNav({ routes, activeId }: BotNavProps): JSX.Element {
  const { t } = useTranslation("app");

  return (
    <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2 py-4">
      {routes.map((route) => {
        const active = route.id === activeId;
        return (
          <li key={route.id}>
            <RouteLink
              to={route.path}
              aria-current={active ? "page" : undefined}
              className={cx(
                "flex items-center rounded-xl px-4 py-3 text-body-md tracking-tight transition-all",
                active
                  ? "border border-primaryBorder bg-primarySubtle font-bold text-primary"
                  : "font-medium text-textMuted hover:bg-surfaceRaised hover:text-primary",
              )}
            >
              {t(`nav.${route.id}`)}
            </RouteLink>
          </li>
        );
      })}
    </ul>
  );
}
